import React from 'react';
import { motion } from 'framer-motion';
import { IoOpenOutline, IoLogoGithub } from 'react-icons/io5';

const ProjectCard = ({ project, index }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: index * 0.1
      }
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      whileHover={{ y: -4, scale: 1.01 }}
      className="group minimal-card rounded-xl overflow-hidden subtle-glow"
    >
      {/* Project Image */}
      <div className="relative overflow-hidden">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {/* Overlay Links */}
        <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {project.liveLink && (
            <motion.a
              href={project.liveLink}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 bg-accent-yellow/90 backdrop-blur-sm rounded-lg flex items-center justify-center text-bg-dark hover:bg-accent-yellow transition-colors duration-300"
              aria-label="View Live Project"
            >
              <IoOpenOutline size={16} />
            </motion.a>
          )}
          
          {project.githubLink && (
            <motion.a
              href={project.githubLink}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 bg-white-1/20 backdrop-blur-sm rounded-lg flex items-center justify-center text-white-1 hover:bg-white-1/30 transition-colors duration-300"
              aria-label="View Source Code"
            >
              <IoLogoGithub size={16} />
            </motion.a>
          )}
        </div>
      </div>

      {/* Project Content */}
      <div className="p-5">
        <h3 className="text-lg font-medium text-white-1 mb-2 group-hover:text-accent-yellow transition-colors duration-300">
          {project.title}
        </h3>
        
        <p className="text-light-gray/80 text-sm mb-4 leading-relaxed line-clamp-2">
          {project.description}
        </p>
        
        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-4">
          {project.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 text-xs bg-white-1/10 text-accent-yellow/80 rounded-md"
            >
              {tag}
            </span>
          ))}
          {project.tags.length > 3 && (
            <span className="px-2 py-1 text-xs text-light-gray/60">
              +{project.tags.length - 3}
            </span>
          )}
        </div>
        
        {/* Links */}
        <div className="flex gap-2">
          {project.liveLink && (
            <a
              href={project.liveLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-accent-yellow hover:text-accent-yellow/80 transition-colors duration-300"
            >
              <IoOpenOutline size={12} />
              Live Demo
            </a>
          )}
          
          {project.githubLink && (
            <a
              href={project.githubLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-light-gray/70 hover:text-white-1 transition-colors duration-300"
            >
              <IoLogoGithub size={12} />
              Source
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectCard;
